import { useEffect, useMemo, useState } from 'react';
import { Truck, Plus } from 'lucide-react';
import { Button, Card, Input, Modal, Pill, useToast } from '@nako/ui';
import { formatMoney } from '@nako/core';
import { useAuthStore } from '@/stores/auth';
import { useRegionStore } from '@/stores/region';
import { supabase } from '@/lib/supabase';

interface Supplier {
  id: string;
  business_id: string;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  payment_terms_days: number;
  outstanding_balance: number;
  on_hold: boolean;
}

export default function Suppliers() {
  const business = useAuthStore((s) => s.business)!;
  const regionCode = useRegionStore((s) => s.code);
  const toast = useToast();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    contact: '',
    phone: '',
    email: '',
    terms: 30,
  });

  useEffect(() => {
    void load();
  }, [business.id]);

  async function load() {
    const { data } = await supabase
      .from('suppliers')
      .select('*')
      .eq('business_id', business.id)
      .order('name');
    setSuppliers((data ?? []) as Supplier[]);
  }

  async function create() {
    if (!form.name) {
      toast.show('Supplier name required', { tone: 'warning' });
      return;
    }
    await supabase.from('suppliers').insert({
      business_id: business.id,
      name: form.name,
      contact_name: form.contact || null,
      email: form.email || null,
      phone: form.phone || null,
      address: null,
      payment_terms_days: form.terms,
      outstanding_balance: 0,
      on_hold: false,
      notes: null,
    });
    setOpen(false);
    setForm({ name: '', contact: '', phone: '', email: '', terms: 30 });
    void load();
  }

  const totalOwed = useMemo(
    () => suppliers.reduce((s, x) => s + x.outstanding_balance, 0),
    [suppliers],
  );

  return (
    <main className="mx-auto max-w-3xl px-4 py-4 md:px-6 md:py-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display flex items-center gap-2 text-2xl">
          <Truck size={22} /> Suppliers
        </h1>
        <Button size="sm" leftIcon={<Plus size={16} />} onClick={() => setOpen(true)}>
          Add
        </Button>
      </div>

      <Card className="bg-ink text-paper mb-4">
        <div className="flex items-center justify-between">
          <span className="text-paper-2">Total payable</span>
          <span className="tnum font-mono text-2xl font-semibold">
            {formatMoney(totalOwed, regionCode)}
          </span>
        </div>
      </Card>

      {suppliers.length === 0 ? (
        <Card className="py-10 text-center">
          <Truck size={32} className="text-muted mx-auto mb-3 opacity-50" />
          <p className="text-muted mb-4">No suppliers yet.</p>
          <Button onClick={() => setOpen(true)} leftIcon={<Plus size={16} />}>
            Add first supplier
          </Button>
        </Card>
      ) : (
        <div className="space-y-2">
          {suppliers.map((s) => (
            <Card key={s.id} compact className="flex items-center justify-between">
              <div>
                <p className="font-medium">{s.name}</p>
                <p className="text-muted text-xs">
                  {s.contact_name ?? 'No contact'} · {s.payment_terms_days}-day terms
                </p>
              </div>
              <div className="text-right">
                <p className="tnum font-mono text-sm font-semibold">
                  {formatMoney(s.outstanding_balance, regionCode)}
                </p>
                {s.outstanding_balance > 0 && <Pill tone="red">owing</Pill>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add supplier">
        <div className="space-y-3">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Input
            label="Contact (optional)"
            value={form.contact}
            onChange={(e) => setForm({ ...form, contact: e.target.value })}
          />
          <Input
            label="Phone (optional)"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <Input
            label="Email (optional)"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <Input
            label="Payment terms (days)"
            type="number"
            numeric
            value={form.terms || ''}
            onChange={(e) => setForm({ ...form, terms: Number(e.target.value) })}
          />
          <Button full onClick={create}>
            Save
          </Button>
        </div>
      </Modal>
    </main>
  );
}
